import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import apiService from '../../services/api';

interface Country {
  id: number;
  name: string;
  code: string;
  currency: string;
  active: boolean;
}

interface Package {
  id?: number;
  country_id: number;
  name: string;
  name_ar?: string;
  description: string;
  description_ar?: string;
  price: number;
  currency: string;
  features: string[];
  features_ar?: string[];
  processing_time: string;
  processing_time_ar?: string;
  popular: boolean;
  active: boolean;
  sort_order: number;
  country_name?: string;
  country_code?: string;
  requirement_template_id?: number;
  template_name?: string;
}

interface Template {
  id: number;
  name: string;
  name_ar?: string;
  description?: string;
  description_ar?: string;
  version: number;
  active: boolean;
  created_at: string;
  updated_at: string;
  step_count?: number;
  package_count?: number;
}

const PackageManagement: React.FC = () => {
  const location = useLocation();
  const [packages, setPackages] = useState<Package[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState<Package | null>(null);
  const [activeTab, setActiveTab] = useState<'english' | 'arabic' | 'template'>('english');
  const [mainTab, setMainTab] = useState<'packages' | 'templates'>('packages');

  // Template management states
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [templateActiveTab, setTemplateActiveTab] = useState<'english' | 'arabic'>('english');
  const [templateFormData, setTemplateFormData] = useState({
    name: '',
    name_ar: '',
    description: '',
    description_ar: '',
    active: true
  });
  const [formData, setFormData] = useState<Partial<Package>>({
    country_id: 0,
    name: '',
    name_ar: '',
    description: '',
    description_ar: '',
    price: 0,
    currency: 'USD',
    features: [''],
    features_ar: [''],
    processing_time: '',
    processing_time_ar: '',
    popular: false,
    active: true,
    sort_order: 0,
    requirement_template_id: 0
  });

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle URL parameters to switch tabs
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tabParam = urlParams.get('tab');
    if (tabParam === 'templates') {
      setMainTab('templates');
    }
  }, [location.search]);

  const fetchData = async () => {
    await Promise.all([fetchPackages(), fetchCountries(), fetchTemplates()]);
    setLoading(false);
  };

  const fetchPackages = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/backend/api/admin/packages/', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      if (data.packages) {
        setPackages(data.packages);
      }
    } catch (error) {    }
  };

  const fetchCountries = async () => {
    try {
      const data = await apiService.getAdminCountries();
      if (data.countries) {
        setCountries(data.countries);
      } else if (data.success && data.countries) {
        setCountries(data.countries);
      }
    } catch (error) {    }
  };

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/backend/api/admin/templates/', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      if (Array.isArray(data)) {
        // Direct array response - show all templates for management, not just active ones
        setTemplates(data);
      } else if (data.templates) {
        setTemplates(data.templates);
      } else if (data.success && data.templates) {
        // Fallback for success wrapper format
        setTemplates(data.templates);
      } else {
        setTemplates([]); // Ensure templates is always an array
      }
    } catch (error) {
      // Error fetching templates
    }
  };

  const openModal = (pkg: Package | null = null) => {
    if (pkg) {
      setEditingPackage(pkg);
      setFormData({
        ...pkg,
        features: Array.isArray(pkg.features) ? pkg.features : [],
        features_ar: Array.isArray(pkg.features_ar) ? pkg.features_ar : [''], // Ensure at least one empty field
        requirement_template_id: pkg.requirement_template_id || 0
      });
    } else {
      setEditingPackage(null);
      setFormData({
        country_id: 0,
        name: '',
        name_ar: '',
        description: '',
        description_ar: '',
        price: 0,
        currency: 'USD',
        features: [''],
        features_ar: [''],
        processing_time: '',
        processing_time_ar: '',
        popular: false,
        active: true,
        sort_order: 0,
        requirement_template_id: 0
      });
    }
    setActiveTab('english'); // Always start with English tab
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingPackage(null);
    setActiveTab('english');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseFloat(value) || 0 : value)
    }));
  };

  const handleFeatureChange = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features?.map((feature, i) => i === index ? value : feature) || []
    }));
  };

  const handleFeatureArChange = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      features_ar: prev.features_ar?.map((feature, i) => i === index ? value : feature) || []
    }));
  };

  const addFeature = () => {
    setFormData(prev => ({
      ...prev,
      features: [...(prev.features || []), ''],
      features_ar: [...(prev.features_ar || []), '']
    }));
  };

  const removeFeature = (index: number) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features?.filter((_, i) => i !== index) || [],
      features_ar: prev.features_ar?.filter((_, i) => i !== index) || []
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate that a requirement template is selected
    if (!formData.requirement_template_id || formData.requirement_template_id === 0) {
      alert('Please select a requirements template. This is required to define the customer requirements gathering process.');
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const method = editingPackage ? 'PUT' : 'POST';
      const url = editingPackage
        ? `/backend/api/admin/packages/?id=${editingPackage.id}`
        : '/backend/api/admin/packages/';

      const submitData = {
        ...formData,
        features: formData.features?.filter(f => f.trim() !== '') || [],
        features_ar: formData.features_ar?.filter(f => f.trim() !== '') || []
      };
      

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(submitData),
      });

      const data = await response.json();
      
      if (data.success) {
        await fetchPackages();
        closeModal();
        alert(editingPackage ? 'Package updated successfully!' : 'Package created successfully!');
      } else {
        alert('Error: ' + (data.error || 'Failed to save package'));
      }
    } catch (error) {      alert('Network error occurred');
    }
  };

  const handleDelete = async (packageId: number) => {
    if (!window.confirm('Are you sure you want to delete this package?')) return;

    try {
      const data = await apiService.deleteAdminPackage(packageId);

      if (data.success) {
        await fetchPackages();
        alert('Package deleted successfully!');
      } else {
        alert('Error: ' + (data.error || 'Failed to delete package'));
      }
    } catch (error) {
      alert('Network error occurred');
    }
  };

  // Template management functions
  const openTemplateModal = (template: Template | null = null) => {
    if (template) {
      setEditingTemplate(template);
      setTemplateFormData({
        name: template.name,
        name_ar: template.name_ar || '',
        description: template.description || '',
        description_ar: template.description_ar || '',
        active: template.active
      });
    } else {
      setEditingTemplate(null);
      setTemplateFormData({
        name: '',
        name_ar: '',
        description: '',
        description_ar: '',
        active: true
      });
    }
    setTemplateActiveTab('english');
    setShowTemplateModal(true);
  };

  const closeTemplateModal = () => {
    setShowTemplateModal(false);
    setEditingTemplate(null);
    setTemplateActiveTab('english');
  };

  const handleTemplateInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setTemplateFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleTemplateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('admin_token');
      const method = editingTemplate ? 'PUT' : 'POST';
      const url = editingTemplate
        ? `/backend/api/admin/templates/?id=${editingTemplate.id}`
        : '/backend/api/admin/templates/';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(templateFormData),
      });

      const data = await response.json();

      if (data.success) {
        await fetchTemplates();
        closeTemplateModal();
        alert(editingTemplate ? 'Template updated successfully!' : 'Template created successfully!');
      } else {
        alert('Error: ' + (data.error || 'Failed to save template'));
      }
    } catch (error) {      alert('Network error occurred');
    }
  };

  const handleTemplateDelete = async (templateId: number) => {
    if (!window.confirm('Are you sure you want to delete this template? This action cannot be undone.')) return;

    try {
      const data = await apiService.deleteAdminTemplate(templateId);

      if (data.success) {
        await fetchTemplates();
        alert('Template deleted successfully!');
      } else {
        alert('Error: ' + (data.error || 'Failed to delete template'));
      }
    } catch (error) {
      alert('Network error occurred');
    }
  };

  const toggleTemplateStatus = async (template: Template) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`/backend/api/admin/templates/?id=${template.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          ...template,
          active: !template.active
        }),
      });

      const data = await response.json();

      if (data.success) {
        await fetchTemplates();
      } else {
        alert('Error: ' + (data.error || 'Failed to update template status'));
      }
    } catch (error) {      alert('Network error occurred');
    }
  };

  // Group packages by country
  const groupedPackages = packages.reduce((groups, pkg) => {
    const countryName = pkg.country_name || 'Unknown Country';
    if (!groups[countryName]) {
      groups[countryName] = [];
    }
    groups[countryName].push(pkg);
    return groups;
  }, {} as Record<string, Package[]>);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" dir="ltr">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Package Management</h2>
            <p className="text-gray-600 mt-1">Manage packages and requirement templates</p>
          </div>
          <div className="flex space-x-3">
            {mainTab === 'packages' && (
              <button
                onClick={() => openModal()}
                className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
              >
                Add New Package
              </button>
            )}
            {mainTab === 'templates' && (
              <button
                onClick={() => openTemplateModal()}
                className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
              >
                Create New Template
              </button>
            )}
          </div>
        </div>

        {/* Main Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setMainTab('packages')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${mainTab === 'packages'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              📦 Packages Management
            </button>
            <button
              onClick={() => setMainTab('templates')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${mainTab === 'templates'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              📝 Requirements Templates
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {mainTab === 'packages' && (
          <div className="space-y-8">
          {Object.entries(groupedPackages).map(([countryName, countryPackages]) => (
            <div key={countryName} className="bg-white rounded-lg shadow overflow-hidden">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  {countryName} ({countryPackages.length} package{countryPackages.length !== 1 ? 's' : ''})
                </h3>
              </div>
              
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Package
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Price
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Template
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {countryPackages.map((pkg) => (
                    <tr key={pkg.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{pkg.name}</div>
                          <div className="text-sm text-gray-500 truncate max-w-xs">{pkg.description}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-medium text-gray-900">
                          {pkg.currency} {pkg.price}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {pkg.template_name ? (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            {pkg.template_name}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">No template</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex space-x-2">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            pkg.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {pkg.active ? 'Active' : 'Inactive'}
                          </span>
                          {!!pkg.popular && (
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                              Popular
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => openModal(pkg)}
                          className="text-primary-600 hover:text-primary-900 mr-4"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(pkg.id!)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
          
            {packages.length === 0 && (
              <div className="bg-white rounded-lg shadow p-8 text-center">
                <p className="text-gray-500">No packages found. Create your first package to get started.</p>
              </div>
            )}
          </div>
        )}

        {/* Templates Tab Content */}
        {mainTab === 'templates' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Template
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Version
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Steps
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Packages
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {templates.map((template) => (
                  <tr key={template.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{template.name}</div>
                        {template.name_ar && (
                          <div className="text-sm text-gray-500" dir="rtl">{template.name_ar}</div>
                        )}
                        {template.description && (
                          <div className="text-sm text-gray-500 truncate max-w-xs">{template.description}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-gray-900">v{template.version}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{template.step_count || 0}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{template.package_count || 0}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => toggleTemplateStatus(template)}
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full cursor-pointer transition-colors ${
                          template.active
                            ? 'bg-green-100 text-green-800 hover:bg-green-200'
                            : 'bg-red-100 text-red-800 hover:bg-red-200'
                        }`}
                      >
                        {template.active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <Link
                          to={`/admin/templates/builder/${template.id}`}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          Build
                        </Link>
                        <button
                          onClick={() => openTemplateModal(template)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          Edit
                        </button>
                        <Link
                          to={`/admin/templates/preview/${template.id}`}
                          className="text-green-600 hover:text-green-900"
                        >
                          Preview
                        </Link>
                        <button
                          onClick={() => handleTemplateDelete(template.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {templates.length === 0 && (
              <div className="p-8 text-center">
                <p className="text-gray-500">No templates found. Create your first template to get started.</p>
              </div>
            )}
          </div>
        )}

        {/* Package Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" dir="ltr">
            <div className="relative top-10 mx-auto p-6 border w-full max-w-4xl shadow-lg rounded-lg bg-white">
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-6">
                  {editingPackage ? 'Edit Package' : 'Add New Package'}
                </h3>

                {/* Tab Navigation */}
                <div className="border-b border-gray-200 mb-6">
                  <nav className="-mb-px flex space-x-8">
                    <button
                      type="button"
                      onClick={() => setActiveTab('english')}
                      className={`py-2 px-1 border-b-2 font-medium text-sm ${
                        activeTab === 'english'
                          ? 'border-primary-500 text-primary-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      English Content
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab('arabic')}
                      className={`py-2 px-1 border-b-2 font-medium text-sm ${
                        activeTab === 'arabic'
                          ? 'border-primary-500 text-primary-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Arabic Content (العربية)
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab('template')}
                      className={`py-2 px-1 border-b-2 font-medium text-sm ${
                        activeTab === 'template'
                          ? 'border-primary-500 text-primary-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Requirements Template
                    </button>
                  </nav>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Basic Information - Always Visible */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-sm font-semibold text-gray-700 mb-4">Basic Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Country *
                        </label>
                        <select
                          name="country_id"
                          value={formData.country_id || ''}
                          onChange={handleInputChange}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        >
                          <option value="">Select Country</option>
                          {countries.map(country => (
                            <option key={country.id} value={country.id}>
                              {country.name}
                            </option>
                          ))}
                        </select>
                        {countries.length === 0 && (
                          <p className="text-sm text-red-600 mt-1">
                            No countries available. Please check the countries API.
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Price *
                        </label>
                        <input
                          type="number"
                          name="price"
                          value={formData.price}
                          onChange={handleInputChange}
                          step="0.01"
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Currency
                        </label>
                        <select
                          name="currency"
                          value={formData.currency}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        >
                          <option value="USD">USD</option>
                          <option value="GBP">GBP</option>
                          <option value="AED">AED</option>
                          <option value="SAR">SAR</option>
                          <option value="EGP">EGP</option>
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Sort Order
                        </label>
                        <input
                          type="number"
                          name="sort_order"
                          value={formData.sort_order}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                      <div className="flex items-end space-x-6">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            name="popular"
                            checked={formData.popular}
                            onChange={handleInputChange}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-700">Popular Package</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            name="active"
                            checked={formData.active}
                            onChange={handleInputChange}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-700">Active</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  
                  {/* Tabbed Content */}
                  <div className="min-h-[400px]">
                    {activeTab === 'english' && (
                      <div className="space-y-6">
                        <h4 className="text-lg font-medium text-gray-900 border-b pb-2">English Content</h4>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Package Name *
                          </label>
                          <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            required
                            placeholder="Enter package name in English"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Description
                          </label>
                          <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            rows={4}
                            placeholder="Enter package description in English"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Processing Time
                          </label>
                          <input
                            type="text"
                            name="processing_time"
                            value={formData.processing_time}
                            onChange={handleInputChange}
                            placeholder="e.g., 7-10 business days"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">
                            Features
                          </label>
                          {formData.features?.map((feature, index) => (
                            <div key={index} className="flex mb-3">
                              <input
                                type="text"
                                value={feature}
                                onChange={(e) => handleFeatureChange(index, e.target.value)}
                                placeholder={`Feature ${index + 1}`}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                              />
                              <button
                                type="button"
                                onClick={() => removeFeature(index)}
                                className="px-4 py-2 bg-red-500 text-white rounded-r-md hover:bg-red-600 transition-colors"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={addFeature}
                            className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                          >
                            + Add Feature
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {activeTab === 'arabic' && (
                      <div className="space-y-6">
                        <h4 className="text-lg font-medium text-gray-900 border-b pb-2">المحتوى بالعربية</h4>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            اسم الحزمة (Package Name)
                          </label>
                          <input
                            type="text"
                            name="name_ar"
                            value={formData.name_ar || ''}
                            onChange={handleInputChange}
                            dir="rtl"
                            placeholder="أدخل اسم الحزمة بالعربية"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            الوصف (Description)
                          </label>
                          <textarea
                            name="description_ar"
                            value={formData.description_ar || ''}
                            onChange={handleInputChange}
                            rows={4}
                            dir="rtl"
                            placeholder="أدخل وصف الحزمة بالعربية"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            مدة المعالجة (Processing Time)
                          </label>
                          <input
                            type="text"
                            name="processing_time_ar"
                            value={formData.processing_time_ar || ''}
                            onChange={handleInputChange}
                            dir="rtl"
                            placeholder="مثلاً، 7-10 أيام عمل"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">
                            الميزات (Features)
                          </label>
                          {formData.features_ar?.map((feature, index) => (
                            <div key={index} className="flex mb-3">
                              <button
                                type="button"
                                onClick={() => removeFeature(index)}
                                className="px-4 py-2 bg-red-500 text-white rounded-l-md hover:bg-red-600 transition-colors"
                              >
                                ×
                              </button>
                              <input
                                type="text"
                                value={feature}
                                onChange={(e) => handleFeatureArChange(index, e.target.value)}
                                placeholder={`ميزة ${index + 1}`}
                                dir="rtl"
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-r-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                              />
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={addFeature}
                            className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                          >
                            + إضافة ميزة
                          </button>
                        </div>
                      </div>
                    )}

                    {activeTab === 'template' && (
                      <div className="space-y-6">
                        <h4 className="text-lg font-medium text-gray-900 border-b pb-2">Requirements Template Assignment</h4>

                        {editingPackage && (!editingPackage.requirement_template_id || editingPackage.requirement_template_id === 0) && (
                          <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-4">
                            <div className="flex items-start">
                              <div className="flex-shrink-0">
                                <span className="text-amber-400 text-lg">⚠️</span>
                              </div>
                              <div className="ml-3">
                                <h3 className="text-sm font-medium text-amber-800">Template Required</h3>
                                <div className="mt-2 text-sm text-amber-700">
                                  <p>This package currently has no requirement template assigned. You must select a template to save changes.</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
                          <div className="flex items-start">
                            <div className="flex-shrink-0">
                              <span className="text-blue-400 text-lg">ℹ️</span>
                            </div>
                            <div className="ml-3">
                              <h3 className="text-sm font-medium text-blue-800">About Requirements Templates</h3>
                              <div className="mt-2 text-sm text-blue-700">
                                <p><strong>Requirements templates are mandatory</strong> - they define the dynamic forms that clients will fill when requesting this package. Each package must have an assigned template to define the customer requirements gathering process.</p>
                                <p className="mt-1 text-xs"><em>Note: Existing packages without templates will need to be updated before they can be edited.</em></p>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select Requirements Template *
                          </label>
                          <select
                            name="requirement_template_id"
                            value={formData.requirement_template_id || ''}
                            onChange={handleInputChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          >
                            <option value="">Select a requirements template</option>
                            {templates.filter(t => t.active).map(template => (
                              <option key={template.id} value={template.id}>
                                {template.name}
                                {template.name_ar && ` / ${template.name_ar}`}
                              </option>
                            ))}
                          </select>
                          {templates.filter(t => t.active).length === 0 && (
                            <p className="text-sm text-red-600 mt-1">
                              No active templates available. Create a template first by switching to the Templates tab.
                            </p>
                          )}
                        </div>

                        {formData.requirement_template_id && (
                          <div className="bg-gray-50 rounded-md p-4">
                            <h5 className="text-sm font-medium text-gray-700 mb-2">Template Preview</h5>
                            <div className="text-sm text-gray-600">
                              <p>Template ID: {formData.requirement_template_id}</p>
                              <p className="mt-1">
                                Selected: {templates.filter(t => t.active).find(t => t.id.toString() === formData.requirement_template_id?.toString())?.name}
                              </p>
                              <div className="mt-3">
                                <a
                                  href={`/admin/templates/preview/${formData.requirement_template_id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center text-primary-600 hover:text-primary-800 text-sm"
                                >
                                  <span className="mr-1">👁️</span>
                                  Preview Template
                                </a>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Form Actions */}
                  <div className="border-t pt-6 bg-gray-50 -mx-6 -mb-6 px-6 pb-6 rounded-b-lg">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-500">
                        <span className="text-red-500">*</span> Required fields
                      </div>
                      <div className="flex space-x-3">

                        <button
                          type="button"
                          onClick={closeModal}
                          className="px-6 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="px-6 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700 transition-colors"
                        >
                          {editingPackage ? 'Update' : 'Create'} Package
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Template Modal */}
        {showTemplateModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" dir="ltr">
            <div className="relative top-20 mx-auto p-6 border w-full max-w-2xl shadow-lg rounded-lg bg-white">
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-6">
                  {editingTemplate ? 'Edit Template' : 'Create New Template'}
                </h3>

                {/* Tab Navigation */}
                <div className="border-b border-gray-200 mb-6">
                  <nav className="-mb-px flex space-x-8">
                    <button
                      type="button"
                      onClick={() => setTemplateActiveTab('english')}
                      className={`py-2 px-1 border-b-2 font-medium text-sm ${
                        templateActiveTab === 'english'
                          ? 'border-primary-500 text-primary-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      English Content
                    </button>
                    <button
                      type="button"
                      onClick={() => setTemplateActiveTab('arabic')}
                      className={`py-2 px-1 border-b-2 font-medium text-sm ${
                        templateActiveTab === 'arabic'
                          ? 'border-primary-500 text-primary-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Arabic Content (العربية)
                    </button>
                  </nav>
                </div>

                <form onSubmit={handleTemplateSubmit} className="space-y-6">
                  {/* Tabbed Content */}
                  <div className="min-h-[300px]">
                    {templateActiveTab === 'english' && (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Template Name *
                          </label>
                          <input
                            type="text"
                            name="name"
                            value={templateFormData.name}
                            onChange={handleTemplateInputChange}
                            required
                            placeholder="Enter template name in English"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Description
                          </label>
                          <textarea
                            name="description"
                            value={templateFormData.description}
                            onChange={handleTemplateInputChange}
                            rows={4}
                            placeholder="Enter template description in English"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>
                      </div>
                    )}

                    {templateActiveTab === 'arabic' && (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            اسم القالب (Template Name)
                          </label>
                          <input
                            type="text"
                            name="name_ar"
                            value={templateFormData.name_ar}
                            onChange={handleTemplateInputChange}
                            dir="rtl"
                            placeholder="أدخل اسم القالب بالعربية"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            الوصف (Description)
                          </label>
                          <textarea
                            name="description_ar"
                            value={templateFormData.description_ar}
                            onChange={handleTemplateInputChange}
                            rows={4}
                            dir="rtl"
                            placeholder="أدخل وصف القالب بالعربية"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Status */}
                  <div className="border-t pt-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="active"
                        checked={templateFormData.active}
                        onChange={handleTemplateInputChange}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">Active Template</span>
                    </label>
                  </div>

                  {/* Form Actions */}
                  <div className="border-t pt-6 bg-gray-50 -mx-6 -mb-6 px-6 pb-6 rounded-b-lg">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-500">
                        <span className="text-red-500">*</span> Required fields
                      </div>
                      <div className="flex space-x-3">
                        <button
                          type="button"
                          onClick={closeTemplateModal}
                          className="px-6 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="px-6 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700 transition-colors"
                        >
                          {editingTemplate ? 'Update' : 'Create'} Template
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default PackageManagement;